/**
 * TranscriptContent — Renders transcript text inside the TranscriptTray.
 *
 * Supports two rendering modes:
 *   1. **Rich mode** — when transcriptionData/session subcollection exists.
 *      Renders utterances with speaker labels, word-level confidence data,
 *      and a quality summary bar.
 *   2. **Plain mode** — falls back to getPreferredTranscript() for older
 *      documents that lack rich data.
 *
 * When advancedTranscript is enabled and word-level data is present,
 * utterances render individual word spans with confidence-based
 * highlighting (yellow for medium, orange-underline for low).
 *
 * Speaker labels use role assignments when available (e.g., "Dr. Smith"
 * instead of "Speaker 1"). See SpeakerRoleAssignment.js for role management.
 *
 * Diarization adjustment:
 *   1. Drag speaker labels and drop before a word to reposition breaks
 *   2. Select text and use floating toolbar to assign a speaker to selection
 *
 * See: docs/features/document-system/transcript-improvements.md
 */
import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import Tooltip from "@mui/material/Tooltip";
import { getData } from "../../utils/firestoreService";
import { auth } from "../../utils/firebase";
import { getPreferredTranscript } from "../../utils/documentUtils";
import { getRoleDisplayLabel } from "./SpeakerRoleAssignment";
import { useFeatureFlags } from "../../contexts/FeatureFlagContext";
import {
  cloneUtterances,
  reassignFromWord,
  reassignWordRange,
  extendPreviousSpeakerTo,
} from "../../utils/diarizationMutations";

/**
 * Sanitize transcript text by converting literal escape sequences to actual characters.
 * This handles cases where text was double-encoded or stored with JSON-escaped characters.
 * 
 * Converts:
 *   - Literal \n (backslash + n) → actual newline
 *   - Literal \" (backslash + quote) → actual quote
 *   - Literal \\ (double backslash) → single backslash
 *   - Literal \t (backslash + t) → actual tab
 * 
 * @param {string} text - The transcript text that may contain literal escape sequences
 * @returns {string} - The sanitized text with proper formatting
 */
const sanitizeTranscriptText = (text) => {
  if (!text || typeof text !== 'string') return text;
  
  // Check if text appears to have literal escape sequences
  // (i.e., contains backslash followed by n, ", \, or t)
  if (!text.includes('\\')) return text;
  
  return text
    // Handle double-escaped sequences first (\\n → \n → newline)
    .replace(/\\\\n/g, '\n')
    .replace(/\\\\"/g, '"')
    .replace(/\\\\t/g, '\t')
    .replace(/\\\\\\/g, '\\')
    // Then handle single-escaped sequences
    .replace(/\\n/g, '\n')
    .replace(/\\"/g, '"')
    .replace(/\\t/g, '\t');
};

/**
 * Strip a leading speaker prefix (e.g., "Doctor: ", "Patient: ", "Speaker 1: ")
 * from utterance text. Used in rich mode only, where a styled label pill already
 * shows the speaker — the inline prefix would be a visual duplicate.
 *
 * See: docs/updates/2026-04-21-transcript-in-text-speaker-labels.md
 */
const SPEAKER_PREFIX_RE = /^\s*(?:Doctor|Patient|Speaker\s*\d+):\s*/i;
const stripSpeakerPrefix = (text) => {
  if (!text || typeof text !== 'string') return text;
  return text.replace(SPEAKER_PREFIX_RE, '');
};

/**
 * Map average confidence to a quality label + color.
 */
export const getQualityInfo = (avg) => {
  if (avg >= 0.95) return { label: "Excellent", className: "quality-excellent" };
  if (avg >= 0.85) return { label: "Good", className: "quality-good" };
  if (avg >= 0.70) return { label: "Fair", className: "quality-fair" };
  return { label: "Poor", className: "quality-poor" };
};

/**
 * Format a speaker label for display.
 * Uses role assignment if available, otherwise falls back to "Speaker N".
 */
const formatSpeaker = (speakerIndex, speakerRoles, patientName, clinicianDisplayName) => {
  if (speakerIndex === null || speakerIndex === undefined) return null;
  
  const role = speakerRoles?.[speakerIndex];
  if (role) {
    return getRoleDisplayLabel(role, patientName, clinicianDisplayName);
  }
  
  return `Speaker ${speakerIndex + 1}`;
};

// --- Confidence phrase detection ---
const LOW_THRESHOLD = 0.60;      // Words below this trigger phrase detection
const NEAR_THRESHOLD = 0.78;     // Words below this extend a phrase
const SEARCH_RADIUS = 5;         // Max words to search in each direction
const SPEAKER_CONF_THRESHOLD = 0.50; // Speaker diarization confidence threshold

/**
 * Detect low-confidence phrases in an utterance.
 * Returns a map of wordIndex -> { inPhrase: true, avgConfidence, isLast }
 * for words that belong to a low-confidence phrase.
 */
const detectPhrases = (words) => {
  if (!words || words.length === 0) return {};

  // Find all low-confidence anchor points
  const lowIndices = [];
  for (let i = 0; i < words.length; i++) {
    if (words[i].confidence < LOW_THRESHOLD) {
      lowIndices.push(i);
    }
  }

  if (lowIndices.length === 0) return {};

  // For each anchor, expand outward while finding "near" words
  const ranges = [];
  for (const anchor of lowIndices) {
    let start = anchor;
    let end = anchor;

    // Expand left
    for (let dist = 1; dist <= SEARCH_RADIUS; dist++) {
      const idx = anchor - dist;
      if (idx < 0) break;
      if (words[idx].confidence < NEAR_THRESHOLD) {
        start = idx;
      } else {
        break;
      }
    }

    // Expand right
    for (let dist = 1; dist <= SEARCH_RADIUS; dist++) {
      const idx = anchor + dist;
      if (idx >= words.length) break;
      if (words[idx].confidence < NEAR_THRESHOLD) {
        end = idx;
      } else {
        break;
      }
    }

    ranges.push({ start, end });
  }

  // Merge overlapping ranges
  ranges.sort((a, b) => a.start - b.start);
  const merged = [];
  for (const r of ranges) {
    if (merged.length === 0 || r.start > merged[merged.length - 1].end + 1) {
      merged.push({ ...r });
    } else {
      merged[merged.length - 1].end = Math.max(merged[merged.length - 1].end, r.end);
    }
  }

  // Build lookup map with average confidence per phrase
  const phraseMap = {};
  for (const { start, end } of merged) {
    let sum = 0;
    for (let i = start; i <= end; i++) {
      sum += words[i].confidence;
    }
    const avg = sum / (end - start + 1);

    for (let i = start; i <= end; i++) {
      phraseMap[i] = {
        inPhrase: true,
        avgConfidence: avg,
        isLast: i === end,
      };
    }
  }

  return phraseMap;
};

/**
 * Count words below 60% confidence from utterance word arrays.
 */
export const countLowConfidenceWords = (utterances) => {
  if (!Array.isArray(utterances)) return 0;
  let count = 0;
  for (const utt of utterances) {
    if (Array.isArray(utt.words)) {
      for (const w of utt.words) {
        if (w.confidence < 0.60) count++;
      }
    }
  }
  return count;
};

/**
 * Auto-growing textarea. Resizes to fit content on mount and on every change.
 */
const AutoGrowTextarea = ({ value, onChange, className }) => {
  const ref = useRef(null);

  const resize = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, []);

  useEffect(() => {
    resize();
  }, [value, resize]);

  return (
    <textarea
      ref={ref}
      className={className}
      value={value}
      onChange={(e) => { onChange(e.target.value); resize(); }}
      rows={1}
      spellCheck
    />
  );
};

/**
 * HTML-escape a string for safe insertion into innerHTML.
 */
const escapeHtml = (s) => s
  .replace(/&/g, "&amp;")
  .replace(/</g, "&lt;")
  .replace(/>/g, "&gt;");

/**
 * Build styled HTML for the contenteditable on initial seed. Each speaker
 * block becomes a styled <div class="transcript-edit-label"> header followed
 * by the body. Subsequent text typed by the user is plain — labels re-style
 * only on next edit-session entry. Good-enough for POC.
 */
const buildEditHtml = (serializedText) => {
  if (!serializedText) return "<div><br></div>";
  const blocks = serializedText.split(/\n\s*\n/);
  return blocks
    .map((block, bi) => {
      const lines = block.split("\n");
      const firstLine = lines[0] || "";
      const rest = lines.slice(1).join("\n");
      const isLabel = /^[^:\n]{1,60}:\s*$/.test(firstLine);

      const parts = [];
      if (bi > 0) parts.push("<div><br></div>"); // blank line separator
      if (isLabel) {
        parts.push(`<div class="transcript-edit-label">${escapeHtml(firstLine)}</div>`);
        if (rest) {
          rest.split("\n").forEach((ln) => {
            parts.push(`<div>${escapeHtml(ln) || "<br>"}</div>`);
          });
        }
      } else {
        block.split("\n").forEach((ln) => {
          parts.push(`<div>${escapeHtml(ln) || "<br>"}</div>`);
        });
      }
      return parts.join("");
    })
    .join("");
};

/**
 * Contenteditable div that lets us style speaker-label lines while editing.
 * Uncontrolled: seeded once via initialHtml, reads plain text on input.
 */
const StyledEditableTranscript = ({ initialHtml, onInput, className, readOnly = false }) => {
  const ref = useRef(null);

  // Seed innerHTML once on mount (or when initialHtml identity changes,
  // which only happens when re-entering edit mode on a new doc).
  useEffect(() => {
    if (ref.current && initialHtml !== undefined) {
      ref.current.innerHTML = initialHtml;
    }
  }, [initialHtml]);

  const handleInput = useCallback((e) => {
    // Replacing a selection can leave body text inside a styled label block.
    // Remove that inherited styling before innerText applies text-transform.
    e.currentTarget.querySelectorAll('.transcript-edit-label').forEach(label => {
      if (!/^[^\r\n]+:\s*$/.test((label.innerText || label.textContent || '').trim())) {
        label.classList.remove('transcript-edit-label');
      }
    });
    // innerText preserves visual line breaks across divs/br tags.
    onInput(e.currentTarget.innerText);
  }, [onInput]);

  return (
    <div
      ref={ref}
      className={className}
      contentEditable={!readOnly}
      role="textbox"
      aria-label="Transcript text"
      aria-multiline="true"
      aria-readonly={readOnly}
      suppressContentEditableWarning
      spellCheck
      onInput={handleInput}
    />
  );
};

/**
 * Serialize utterances into a single editable string with inline speaker labels.
 * Different speakers are separated by a blank line; consecutive utterances from
 * the same speaker are merged into one block. The label sits on its own line
 * (uppercase + trailing colon) so the textarea visually mirrors the read view.
 *
 * Example output:
 *   ARNOLD DONOVAN:
 *   I respected and I was depressed…
 *
 *   DR. JIM:
 *   Right. And you've talked about…
 */
const serializeUtterancesForEdit = (utts, effectiveRoles, patientName, clinicianDisplayName) => {
  const blocks = [];
  let lastSpeaker; // undefined means "no prior"
  utts.forEach((u) => {
    const text = (u.transcript || "").trim();
    if (!text) return;
    if (u.speaker !== lastSpeaker || blocks.length === 0) {
      const label = formatSpeaker(u.speaker, effectiveRoles, patientName, clinicianDisplayName);
      const header = label ? `${label.toUpperCase()}:\n` : "";
      blocks.push(`${header}${text}`);
    } else {
      // Same speaker as previous block — append with a space.
      blocks[blocks.length - 1] = `${blocks[blocks.length - 1]} ${text}`;
    }
    lastSpeaker = u.speaker;
  });
  return blocks.join("\n\n");
};

/**
 * Parse edited transcript text back into an utterances array. Splits on blank
 * lines and attempts to match a leading "<Label>:" prefix against known speaker
 * labels. Unrecognized prefixes are kept inline as body text, and the utterance
 * inherits the previous speaker (or `fallbackSpeaker` for the first block).
 */
const parseEditedTextToUtterances = (
  text,
  knownSpeakers,
  effectiveRoles,
  patientName,
  clinicianDisplayName,
  fallbackSpeaker
) => {
  // Build a case-insensitive label -> speakerIndex lookup covering both role
  // labels ("Doctor", "Dr. Smith") and the generic "Speaker N" form.
  const labelMap = new Map();
  knownSpeakers.forEach((si) => {
    const roleLabel = formatSpeaker(si, effectiveRoles, patientName, clinicianDisplayName);
    if (roleLabel) labelMap.set(roleLabel.trim().toLowerCase(), si);
    labelMap.set(`speaker ${si + 1}`.toLowerCase(), si);
  });

  const blocks = text
    .split(/\n\s*\n+/)
    .map((b) => b.trim())
    .filter(Boolean);

  if (blocks.length === 0) {
    return [{ transcript: "", words: [], edited: true, speaker: fallbackSpeaker }];
  }

  const out = [];
  let lastSpeaker = fallbackSpeaker;
  for (const block of blocks) {
    let speaker = lastSpeaker;
    let body = block;
    const m = block.match(/^([^:\n]{1,60}):\s*([\s\S]*)$/);
    if (m) {
      const key = m[1].trim().toLowerCase();
      if (labelMap.has(key)) {
        speaker = labelMap.get(key);
        body = m[2];
      }
    }
    out.push({
      transcript: body.trim(),
      words: [],
      edited: true,
      speaker,
    });
    lastSpeaker = speaker;
  }
  return out;
};

const TranscriptContent = ({
  documentData,
  patientId,
  patientName = "",
  clinicianDisplayName = "",
  speakerRoles = {},
  settings,
  advancedTranscript = false,
  hideSpeakerLabels = false, // Document-level toggle to hide speaker labels entirely
  isEditing = false, // When true, utterances render as editable textareas
  isSavingEdits = false,
  onEditedUtterancesChange, // Called on every keystroke while editing with the current edited utterances
  onQualityData,
  onUtterancesChange, // Called when user adjusts diarization breaks
  utterancesOverride = null, // When parent has newer utterances (e.g. after save), sync to these
}) => {
  const { flags = {} } = useFeatureFlags();
  const debugMode = !!flags.debugMode;
  const [richData, setRichData] = useState(null);
  const [qualityData, setQualityData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchAttempted, setFetchAttempted] = useState(false);

  // Local utterances state for diarization editing
  const [localUtterances, setLocalUtterances] = useState(null);

  // Edit-mode state: a single editable buffer for the whole transcript.
  // Speaker labels are serialized inline ("Doctor: ...") and parsed back on
  // change so speaker pills survive the save. Word-level timing is dropped.
  const [editedText, setEditedText] = useState(null);
  // Stable HTML seed for the contenteditable — only changes per edit session
  // (identity change forces contenteditable to re-init; avoids caret jumps).
  const [editHtmlSeed, setEditHtmlSeed] = useState(null);
  
  // Drag state for speaker label repositioning
  const [draggingSpeaker, setDraggingSpeaker] = useState(null); // { speakerIndex, label, sourceUttIndex }
  const draggingSpeakerRef = useRef(null);
  const [dropTarget, setDropTarget] = useState(null); // { uttIndex, wordIndex }
  const dropTargetRef = useRef(null);

  // Selection state for assigning speaker to highlighted text
  const [selection, setSelection] = useState(null); // { startUtt, startWord, endUtt, endWord, rect }
  const contentRef = useRef(null);

  const docId = documentData?.id;

  // Clear selection/drag state when speaker labels are hidden
  useEffect(() => {
    if (hideSpeakerLabels) {
      draggingSpeakerRef.current = null;
      setSelection(null);
      setDraggingSpeaker(null);
      setDropTarget(null);
    }
  }, [hideSpeakerLabels]);

  useEffect(() => {
    if (isEditing) {
      draggingSpeakerRef.current = null;
      setDraggingSpeaker(null);
      setDropTarget(null);
    }
  }, [isEditing]);

  useEffect(() => {
    dropTargetRef.current = dropTarget;
  }, [dropTarget]);

  // Fetch rich transcription data + quality report from subcollection
  useEffect(() => {
    if (!patientId || !docId) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    const basePath = `patients/${patientId}/documents/${docId}/transcriptionData`;

    const fetchAll = async () => {
      try {
        // Try backend API first (GCS-backed data)
        let session = null;
        try {
          const token = await auth.currentUser?.getIdToken();
          if (token) {
            const resp = await fetch(`/api/transcript-data/${patientId}/${docId}`, {
              headers: { Authorization: `Bearer ${token}` },
            });
            if (resp.ok) {
              session = await resp.json();
            }
          }
        } catch {
          // API unavailable — fall back to Firestore
        }

        // Legacy fallback: read directly from Firestore
        if (!session) {
          session = await getData(`${basePath}/session`, { allowMissing: true }).catch(() => null);
        }

        const quality = await getData(`${basePath}/quality`, { allowMissing: true }).catch(() => null);

        if (!cancelled) {
          setRichData(session || null);
          setQualityData(quality || null);
        }
      } catch {
        if (!cancelled) {
          setRichData(null);
          setQualityData(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
          setFetchAttempted(true);
        }
      }
    };

    fetchAll();
    return () => { cancelled = true; };
  }, [patientId, docId]);

  // Sync local utterances when richData loads (for editable diarization)
  useEffect(() => {
    if (richData?.utterances?.length > 0) {
      setLocalUtterances(cloneUtterances(richData.utterances));
    } else {
      setLocalUtterances(null);
    }
  }, [richData]);

  // Re-sync local utterances when the parent hands down a newer version
  // (e.g. after the tray saves text edits and exits edit mode).
  useEffect(() => {
    if (utterancesOverride && Array.isArray(utterancesOverride)) {
      setLocalUtterances(cloneUtterances(utterancesOverride));
    }
  }, [utterancesOverride]);

  // Merge speaker roles from props (live state) or richData (saved).
  // Computed early so edit-mode serialization can use it.
  const effectiveRoles = useMemo(
    () => (Object.keys(speakerRoles).length > 0
      ? speakerRoles
      : (richData?.speakerRoles || {})),
    [speakerRoles, richData?.speakerRoles]
  );

  // Initialize / clear the single editable text buffer when entering/leaving
  // edit mode. Seeds from current utterances (including any unsaved
  // diarization edits) and inlines speaker labels so they survive the save.
  useEffect(() => {
    if (isEditing) {
      if (editedText !== null) return; // already seeded
      const seed = localUtterances || richData?.utterances || [];
      let seedText;
      if (seed.length > 0) {
        seedText = serializeUtterancesForEdit(
          seed,
          effectiveRoles,
          patientName,
          clinicianDisplayName
        );
      } else {
        seedText = richData?.transcript || "";
      }
      setEditedText(seedText);
      setEditHtmlSeed(buildEditHtml(seedText));
    } else {
      setEditedText(null);
      setEditHtmlSeed(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditing]);

  // Emit parsed utterances to parent so the tray can POST on Save.
  const handleEditedTextChange = useCallback((newText) => {
    setEditedText(newText);
    if (!onEditedUtterancesChange) return;

    const base = localUtterances || richData?.utterances || [];
    const knownSpeakers = Array.from(
      new Set(base.map((u) => u.speaker).filter((s) => s !== undefined && s !== null))
    ).sort((a, b) => a - b);
    const fallbackSpeaker = base[0]?.speaker;

    const parsed = parseEditedTextToUtterances(
      newText,
      knownSpeakers,
      effectiveRoles,
      patientName,
      clinicianDisplayName,
      fallbackSpeaker
    );
    queueMicrotask(() => onEditedUtterancesChange(parsed));
  }, [
    onEditedUtterancesChange,
    localUtterances,
    richData,
    effectiveRoles,
    patientName,
    clinicianDisplayName,
  ]);

  // ---------- Speaker label drag handlers ----------
  const handleLabelDragStart = useCallback((e, speakerIndex, label, sourceUttIndex) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", label);
    const next = { speakerIndex, label, sourceUttIndex };
    draggingSpeakerRef.current = next;
    setDraggingSpeaker(next);
  }, []);

  const handleLabelDragEnd = useCallback(() => {
    draggingSpeakerRef.current = null;
    setDraggingSpeaker(null);
    setDropTarget(null);
  }, []);

  const handleWordDragOver = useCallback((e, uttIndex, wordIndex) => {
    if (!(draggingSpeakerRef.current || draggingSpeaker)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDropTarget((prev) => {
      if (prev && prev.uttIndex === uttIndex && prev.wordIndex === wordIndex) {
        return prev;
      }
      dropTargetRef.current = { uttIndex, wordIndex };
      return { uttIndex, wordIndex };
    });
  }, [draggingSpeaker]);

  const handleTranscriptDragOverCapture = useCallback((e) => {
    if (!(draggingSpeakerRef.current || draggingSpeaker)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  }, [draggingSpeaker]);

  const handleWordDragLeave = useCallback(() => {
    setDropTarget(null);
  }, []);

  const handleWordDrop = useCallback((e, uttIndex, wordIndex) => {
    e.preventDefault();
    if (!localUtterances) return;

    const activeDrag = draggingSpeakerRef.current || draggingSpeaker;
    if (!activeDrag) return;

    const { sourceUttIndex, speakerIndex } = activeDrag;

    // Determine if dragging UP (drop before source) or DOWN (drop after source)
    const isDraggingDown =
      uttIndex > sourceUttIndex ||
      (uttIndex === sourceUttIndex && wordIndex > 0);

    let newUtterances;

    if (isDraggingDown && sourceUttIndex > 0) {
      // Dragging DOWN: extend previous speaker to the drop point
      newUtterances = extendPreviousSpeakerTo(
        localUtterances,
        sourceUttIndex,
        uttIndex,
        wordIndex
      );
    } else {
      // Dragging UP: reassign from drop point forward to the dragged speaker
      newUtterances = reassignFromWord(
        localUtterances,
        uttIndex,
        wordIndex,
        speakerIndex
      );
    }

    setLocalUtterances(newUtterances);
    onUtterancesChange?.(newUtterances);
    draggingSpeakerRef.current = null;
    setDraggingSpeaker(null);
    setDropTarget(null);
  }, [draggingSpeaker, localUtterances, onUtterancesChange]);

  const resolveDropTargetFromEvent = useCallback((eventTarget) => {
    if (!eventTarget || !(eventTarget instanceof Element)) return null;
    const wordEl = eventTarget.closest("[data-utt-index][data-word-index]");
    if (!wordEl) return null;

    const uttIndex = parseInt(wordEl.getAttribute("data-utt-index"), 10);
    const wordIndex = parseInt(wordEl.getAttribute("data-word-index"), 10);
    if (Number.isNaN(uttIndex) || Number.isNaN(wordIndex)) return null;

    return { uttIndex, wordIndex };
  }, []);

  const handleTranscriptDropCapture = useCallback((e) => {
    if (!localUtterances) return;

    const activeDrag = draggingSpeakerRef.current || draggingSpeaker;
    if (!activeDrag) return;

    e.preventDefault();
    e.stopPropagation();

    const target = resolveDropTargetFromEvent(e.target) || dropTargetRef.current;
    if (!target) return;

    const { sourceUttIndex, speakerIndex } = activeDrag;
    const isDraggingDown =
      target.uttIndex > sourceUttIndex ||
      (target.uttIndex === sourceUttIndex && target.wordIndex > 0);

    let newUtterances;
    if (isDraggingDown && sourceUttIndex > 0) {
      newUtterances = extendPreviousSpeakerTo(
        localUtterances,
        sourceUttIndex,
        target.uttIndex,
        target.wordIndex
      );
    } else {
      newUtterances = reassignFromWord(
        localUtterances,
        target.uttIndex,
        target.wordIndex,
        speakerIndex
      );
    }

    setLocalUtterances(newUtterances);
    onUtterancesChange?.(newUtterances);
    draggingSpeakerRef.current = null;
    dropTargetRef.current = null;
    setDraggingSpeaker(null);
    setDropTarget(null);
  }, [draggingSpeaker, localUtterances, onUtterancesChange, resolveDropTargetFromEvent]);

  // ---------- Text selection handlers ----------
  const handleSelectionChange = useCallback(() => {
    // Disable selection-based speaker assignment when labels are hidden
    if (!advancedTranscript || !contentRef.current || hideSpeakerLabels) return;

    const sel = window.getSelection();
    if (!sel || sel.isCollapsed || sel.rangeCount === 0) {
      setSelection(null);
      return;
    }

    const range = sel.getRangeAt(0);
    
    // Check if selection is within our transcript content
    if (!contentRef.current.contains(range.commonAncestorContainer)) {
      setSelection(null);
      return;
    }

    // Find the word wrappers that contain the selection endpoints
    const findWordData = (node) => {
      let el = node.nodeType === Node.TEXT_NODE ? node.parentElement : node;
      while (el && el !== contentRef.current) {
        if (el.dataset?.uttIndex !== undefined && el.dataset?.wordIndex !== undefined) {
          return {
            uttIndex: parseInt(el.dataset.uttIndex, 10),
            wordIndex: parseInt(el.dataset.wordIndex, 10),
          };
        }
        el = el.parentElement;
      }
      return null;
    };

    const startData = findWordData(range.startContainer);
    const endData = findWordData(range.endContainer);

    if (!startData || !endData) {
      setSelection(null);
      return;
    }

    // Get position for floating toolbar
    const rect = range.getBoundingClientRect();
    const contentRect = contentRef.current.getBoundingClientRect();

    setSelection({
      startUtt: startData.uttIndex,
      startWord: startData.wordIndex,
      endUtt: endData.uttIndex,
      endWord: endData.wordIndex,
      rect: {
        top: rect.top - contentRect.top,
        left: rect.left - contentRect.left + rect.width / 2,
      },
    });
  }, [advancedTranscript, hideSpeakerLabels]);

  // Listen for selection changes
  useEffect(() => {
    if (!advancedTranscript || hideSpeakerLabels) return;

    const handleMouseUp = () => {
      // Small delay to let selection finalize
      setTimeout(handleSelectionChange, 10);
    };

    document.addEventListener("mouseup", handleMouseUp);
    return () => document.removeEventListener("mouseup", handleMouseUp);
  }, [advancedTranscript, hideSpeakerLabels, handleSelectionChange]);

  // Assign selected words to a speaker
  const handleAssignSpeaker = useCallback((speakerIndex) => {
    if (!selection || !localUtterances) return;

    const newUtterances = reassignWordRange(
      localUtterances,
      selection.startUtt,
      selection.startWord,
      selection.endUtt,
      selection.endWord,
      speakerIndex
    );

    setLocalUtterances(newUtterances);
    onUtterancesChange?.(newUtterances);
    setSelection(null);
    window.getSelection()?.removeAllRanges();
  }, [selection, localUtterances, onUtterancesChange]);

  // Get unique speakers from utterances for toolbar
  const availableSpeakers = React.useMemo(() => {
    if (!localUtterances) return [];
    const speakers = new Set();
    localUtterances.forEach(utt => {
      if (utt.speaker !== undefined) speakers.add(utt.speaker);
    });
    return Array.from(speakers).sort((a, b) => a - b);
  }, [localUtterances]);

  // Pre-compute phrase maps for each utterance (for confidence highlighting)
  const phraseMaps = useMemo(() => {
    const utts = localUtterances || richData?.utterances || [];
    return utts.map((utt) => detectPhrases(utt.words));
  }, [localUtterances, richData?.utterances]);

  // Notify parent of quality data once after initial fetch.
  // Only fires when richData/qualityData change (not on every render) to avoid
  // overwriting live speaker-role preview state with stale saved values.
  useEffect(() => {
    if (onQualityData && fetchAttempted) {
      onQualityData({ richData, qualityData });
    }
  }, [richData, qualityData, fetchAttempted]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------- loading ----------
  if (loading) {
    return <div className="transcript-content-loading">Loading transcript…</div>;
  }

  const retentionDeleted = richData?.retentionDeleted === true
    || richData?._metadata?.retentionDeleted === true;
  if (retentionDeleted) {
    const retentionDays = richData?._metadata?.retentionDays || richData?.retentionDays;
    return (
      <div className="transcript-content-empty" role="status">
        This transcript was automatically deleted{retentionDays ? ` ${retentionDays} days` : ''} after the session to protect patient privacy. Your finished note is unaffected.
      </div>
    );
  }

  // Use local utterances for rendering (supports diarization editing).
  const utterances = localUtterances || richData?.utterances || [];

  // Check if a word is within the current selection
  const isWordSelected = (uttIndex, wordIndex) => {
    if (!selection) return false;
    const { startUtt, startWord, endUtt, endWord } = selection;
    
    // Single utterance selection
    if (startUtt === endUtt && uttIndex === startUtt) {
      return wordIndex >= startWord && wordIndex <= endWord;
    }
    
    // Multi-utterance selection
    if (uttIndex === startUtt) return wordIndex >= startWord;
    if (uttIndex === endUtt) return wordIndex <= endWord;
    return uttIndex > startUtt && uttIndex < endUtt;
  };

  // ---------- rich mode ----------
  if (utterances.length > 0) {
    return (
      <div 
        ref={contentRef}
        onDragOverCapture={handleTranscriptDragOverCapture}
        onDropCapture={handleTranscriptDropCapture}
        className={`transcript-content${draggingSpeaker ? " dragging-speaker" : ""}${selection && !isEditing ? " has-selection" : ""}${isEditing ? " is-editing" : ""}`}
      >
        {/* Floating speaker picker toolbar (disabled while editing text) */}
        {!isEditing && selection && availableSpeakers.length > 0 && (
          <div 
            className="speaker-picker-toolbar"
            style={{ top: selection.rect.top - 40, left: selection.rect.left }}
          >
            <span className="speaker-picker-label">Assign to:</span>
            {availableSpeakers.map(speakerIdx => {
              const label = formatSpeaker(speakerIdx, effectiveRoles, patientName, clinicianDisplayName);
              return (
                <button
                  key={speakerIdx}
                  className="speaker-picker-btn"
                  onClick={() => handleAssignSpeaker(speakerIdx)}
                  title={`Assign selection to ${label}`}
                >
                  {label}
                </button>
              );
            })}
          </div>
        )}

        {/* Edit mode: one styled contenteditable for the full transcript */}
        {isEditing ? (
          <div className="transcript-utterances">
            <StyledEditableTranscript
              className="transcript-full-editor"
              initialHtml={editHtmlSeed ?? ""}
              onInput={handleEditedTextChange}
              readOnly={isSavingEdits}
            />
            <p className="transcript-edit-hint">
              Keep each speaker's <code>LABEL:</code> header to preserve speaker pills. Separate speakers with a blank line.
            </p>
          </div>
        ) : (
        /* Utterances */
        <div className="transcript-utterances">
          {utterances.map((utt, i) => {
            const speaker = formatSpeaker(utt.speaker, effectiveRoles, patientName, clinicianDisplayName);
            const prevSpeaker = i > 0 ? formatSpeaker(utterances[i - 1].speaker, effectiveRoles, patientName, clinicianDisplayName) : null;
            const speakerChanged = speaker && speaker !== prevSpeaker;
            const showLabel = advancedTranscript && speakerChanged && !hideSpeakerLabels;
            const hasWordData = advancedTranscript && Array.isArray(utt.words) && utt.words.length > 0;
            const canDragSpeakerBreak = hasWordData && advancedTranscript && !isEditing;
            const phraseMap = phraseMaps[i] || {};

            return (
              <div key={i} className={`transcript-utterance${speakerChanged && hideSpeakerLabels && i > 0 ? " speaker-change" : ""}`} data-speaker={utt.speaker}>
                {showLabel && (
                  <div
                    className={`transcript-speaker-label${canDragSpeakerBreak ? " draggable" : ""}`}
                    draggable={canDragSpeakerBreak}
                    onDragStart={(e) => !isEditing && handleLabelDragStart(e, utt.speaker, speaker, i)}
                    onDragEnd={handleLabelDragEnd}
                    title={canDragSpeakerBreak
                      ? "Drag to reposition speaker break"
                      : "Speaker break repositioning unavailable for this transcript"}
                  >
                    {speaker}
                  </div>
                )}
                {hasWordData ? (
                  <p className="transcript-utterance-text">
                    {utt.words.map((w, wi) => {
                      const isDropTarget = dropTarget?.uttIndex === i && dropTarget?.wordIndex === wi;
                      const isSelected = isWordSelected(i, wi);
                      const phraseInfo = phraseMap[wi];
                      const inPhrase = debugMode && phraseInfo?.inPhrase;
                      const avgPct = phraseInfo ? Math.round(phraseInfo.avgConfidence * 100) : 0;
                      const lowSpeakerConf = debugMode && typeof w.speaker_confidence === 'number' && w.speaker_confidence < SPEAKER_CONF_THRESHOLD;
                      const speakerPct = typeof w.speaker_confidence === 'number' ? Math.round(w.speaker_confidence * 100) : null;

                      const wordSpan = (
                        <span
                          key={wi}
                          className={`transcript-word-wrapper${isDropTarget ? " drop-target" : ""}${isSelected ? " selected" : ""}${inPhrase ? " low-confidence-phrase" : ""}${lowSpeakerConf ? " low-speaker-confidence" : ""}`}
                          data-utt-index={i}
                          data-word-index={wi}
                          onDragOver={(e) => handleWordDragOver(e, i, wi)}
                          onDragLeave={handleWordDragLeave}
                          onDrop={(e) => handleWordDrop(e, i, wi)}
                        >
                          <span className="transcript-word">{w.word} </span>
                        </span>
                      );

                      // Wrap with tooltip for any word in a low-confidence phrase or low speaker confidence
                      if (inPhrase || lowSpeakerConf) {
                        const parts = [];
                        if (inPhrase) parts.push(`Avg confidence: ${avgPct}%`);
                        if (lowSpeakerConf) parts.push(`Speaker confidence: ${speakerPct}%`);
                        return (
                          <Tooltip
                            key={`tooltip-${wi}`}
                            title={parts.join(' · ')}
                            arrow
                            placement="top"
                            enterDelay={300}
                            leaveDelay={0}
                          >
                            {wordSpan}
                          </Tooltip>
                        );
                      }

                      return wordSpan;
                    })}
                  </p>
                ) : (
                  <p className="transcript-utterance-text">{stripSpeakerPrefix(sanitizeTranscriptText(utt.transcript))}</p>
                )}
              </div>
            );
          })}
        </div>
        )}
      </div>
    );
  }

  // ---------- plain mode (fallback) ----------
  // Prefer raw Deepgram transcript when available; fall back to AI-processed fields
  const plainText = richData?.transcript || getPreferredTranscript(documentData, settings);

  if (!plainText) {
    return (
      <div className="transcript-content-empty">
        No transcript data available for this document.
      </div>
    );
  }

  // Sanitize escape sequences (e.g., literal \n, \") that may be in stored data
  const sanitizedText = sanitizeTranscriptText(plainText);

  return (
    <div className="transcript-content">
      <div className="transcript-plain-text">{sanitizedText}</div>
    </div>
  );
};

export default React.memo(TranscriptContent);
